import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { ISeguroNegociacaoRetorno, NegociacaoStatusEnum } from "@/models/consignado/proposal-billings.model";

// Formulário de sinistro da CCB (seção "Sinistro", HIS-030 / TAR-061). Criar e
// editar no mesmo modal: com `sinistro` preenche os campos e o botão vira
// "Salvar". O POST exige os três campos; o PUT aceita qualquer subconjunto,
// mas aqui sempre mandamos os três.

export type SinistroFormValues = {
  // Valor do <input type="datetime-local"> ("yyyy-MM-ddTHH:mm", hora local).
  data_contato: string;
  status: NegociacaoStatusEnum;
  descricao: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Presente = edição.
  sinistro?: ISeguroNegociacaoRetorno | null;
  onSubmit: (values: SinistroFormValues) => Promise<unknown>;
  isSaving: boolean;
};

const paraDatetimeLocal = (data?: string) => {
  if (!data) return "";
  const d = new Date(data);
  return isNaN(d.getTime()) ? "" : format(d, "yyyy-MM-dd'T'HH:mm");
};

const valoresIniciais = (sinistro?: ISeguroNegociacaoRetorno | null): SinistroFormValues => ({
  data_contato: paraDatetimeLocal(sinistro?.data_contato),
  status: (sinistro?.status as NegociacaoStatusEnum) ?? NegociacaoStatusEnum.PENDENTE,
  descricao: sinistro?.descricao ?? "",
});

export default function SinistroFormModal({ isOpen, onClose, sinistro, onSubmit, isSaving }: Props) {
  const [values, setValues] = useState<SinistroFormValues>(() => valoresIniciais(sinistro));
  const edicao = !!sinistro;

  useEffect(() => {
    if (isOpen) setValues(valoresIniciais(sinistro));
  }, [isOpen, sinistro]);

  const set = <K extends keyof SinistroFormValues>(campo: K, valor: SinistroFormValues[K]) =>
    setValues((prev) => ({ ...prev, [campo]: valor }));

  const invalido = !values.data_contato || !values.descricao.trim() || !values.status;

  const handleSubmit = async () => {
    if (invalido) return;
    await onSubmit(values);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{edicao ? "Editar sinistro" : "Adicionar novo sinistro"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex flexDirection="column" gap={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Data do Contato</FormLabel>
              <Input
                type="datetime-local"
                value={values.data_contato}
                onChange={(e) => set("data_contato", e.target.value)}
                fontSize="sm"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Status</FormLabel>
              <Select
                value={values.status}
                onChange={(e) => set("status", e.target.value as NegociacaoStatusEnum)}
                fontSize="sm"
              >
                <option value={NegociacaoStatusEnum.PENDENTE}>Pendente</option>
                <option value={NegociacaoStatusEnum.EM_ANDAMENTO}>Em andamento</option>
                <option value={NegociacaoStatusEnum.CONCLUIDA}>Concluída</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Descrição</FormLabel>
              <Textarea
                value={values.descricao}
                onChange={(e) => set("descricao", e.target.value)}
                placeholder="Descreva o andamento do sinistro..."
                fontSize="sm"
                rows={4}
              />
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm" isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            size="sm"
            isDisabled={invalido}
            isLoading={isSaving}
          >
            {edicao ? "Salvar" : "Adicionar"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
